/* eslint-disable jsdoc/require-jsdoc, jsdoc/require-param, jsdoc/require-returns */
import { DEV_NEW_DOMAIN_URL } from '../environment';
import { getTokenFromSessionStorage } from './session-storage-service';

/**
 * Client for the BLE live-ping push stream (`GET /ble-pings/admin/stream`,
 * Server-Sent Events).
 *
 * Why fetch and not `EventSource`: EventSource cannot send an Authorization
 * header, and putting the JWT in the query string would leak it into proxy
 * and access logs. A streamed fetch carries the header like every other call.
 *
 * Reconnects on its own with capped, jittered backoff. A 401/403 is NOT
 * retried (the session or the permission is gone — hammering would not help).
 * After any reconnect the caller gets `onReconnected` so it can backfill the
 * gap from the REST listing; the stream itself only carries new pings.
 */

export type StreamStatus = 'connecting' | 'live' | 'reconnecting' | 'denied' | 'closed';

export interface BleStreamHandlers<T> {
  onPings: (pings: T[]) => void;
  onStatus: (status: StreamStatus) => void;
  /** Fired after a reconnect, or when the server says this viewer dropped pings. */
  onGap: () => void;
}

const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

/** Opens the stream; returns a function that closes it for good. */
export function openBleStream<T>(serial: string, handlers: BleStreamHandlers<T>): () => void {
  let closed = false;
  let attempt = 0;
  let controller: AbortController | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const url = new URL(`${DEV_NEW_DOMAIN_URL}/ble-pings/admin/stream`, window.location.origin);
  if (serial) url.searchParams.set('serial', serial);

  const dispatch = (event: string, data: string): void => {
    if (event === 'ready') {
      handlers.onStatus('live');
      if (attempt > 0) handlers.onGap();
      attempt = 0;
    } else if (event === 'pings') {
      try {
        const parsed = JSON.parse(data);
        if (Array.isArray(parsed) && parsed.length > 0) handlers.onPings(parsed as T[]);
      } catch {
        // A malformed frame is skipped; the next one is independent.
      }
    } else if (event === 'dropped') {
      handlers.onGap();
    }
  };

  const scheduleRetry = (): void => {
    if (closed) return;
    handlers.onStatus('reconnecting');
    attempt += 1;
    const base = Math.min(MAX_BACKOFF_MS, MIN_BACKOFF_MS * 2 ** (attempt - 1));
    const delay = base / 2 + Math.random() * (base / 2);
    // eslint-disable-next-line no-use-before-define
    retryTimer = setTimeout(connect, delay);
  };

  async function connect(): Promise<void> {
    if (closed) return;
    controller = new AbortController();
    if (attempt === 0) handlers.onStatus('connecting');
    try {
      const res = await fetch(url.toString(), {
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${getTokenFromSessionStorage()}`
        },
        signal: controller.signal,
        cache: 'no-store'
      });
      if (res.status === 401 || res.status === 403) {
        handlers.onStatus('denied');
        return;
      }
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let event = 'message';
      let data: string[] = [];
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let newline = buffer.indexOf('\n');
        while (newline >= 0) {
          const line = buffer.slice(0, newline).replace(/\r$/, '');
          buffer = buffer.slice(newline + 1);
          if (line === '') {
            if (data.length > 0) dispatch(event, data.join('\n'));
            event = 'message';
            data = [];
          } else if (line.startsWith('event:')) {
            event = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            data.push(line.slice(5).replace(/^ /, ''));
          }
          // Lines starting with ':' are heartbeats; nothing to do.
          newline = buffer.indexOf('\n');
        }
      }
      // The server ended the stream (its lifetime cap) — reconnect.
      scheduleRetry();
    } catch {
      if (!closed) scheduleRetry();
    }
  }

  connect();

  return () => {
    closed = true;
    if (retryTimer) clearTimeout(retryTimer);
    controller?.abort();
    handlers.onStatus('closed');
  };
}
